import { Builder } from '@builder.io/react'
import React from 'react'

import CdnImage from 'components/util/CdnImage'
import PinkCross from '../../../images/builder/cross-alt.svg'
import BlueTick from '../../../images/builder/tick-alt.svg'
import './index.css'

interface Competitor {
  name: string
  code: string
  builderLogo: any
  altText: string
}

interface Availability {
  compCode: string
  isBool: boolean
  availabilityText: string
  availability: boolean
  featureText: string
}

interface Feature {
  featureName: string
  icon: any
  availability: Availability[]
}

interface Props {
  competitors: Competitor[]
  features: Feature[]
  backgroundColor: string
  featureTextColor: string
  borderColor: string
  headerBackgroundColor: string
  tableBackgroundColor: string
  builderDesktopLogo: any
  availableFeatureAltText: string
  unavailableFeatureAltText: string
}

interface AvailabilityRow {
  index: number
  competitor: Competitor
  feature: Feature
  featureTextColor: string
  borderColor: string
  widthSize: string
  availableFeatureAltText: string
  unavailableFeatureAltText: string

  backgroundColor: string | undefined
}

const DesktopRow = ({
  index,
  competitor,
  feature,
  featureTextColor,
  widthSize,
  availableFeatureAltText = '',
  unavailableFeatureAltText = '',

  backgroundColor
}: AvailabilityRow) => {
  const availabilityItem: Availability | any = feature.availability.find(a => a.compCode === competitor.code)

  const bgColor = index !== 0 ? backgroundColor : undefined

  return (
    <td
      key={index}
      style={{ minWidth: widthSize, backgroundColor: bgColor }}
      className={`max-w-[250px] p-1 text-center sm:py-4 md:px-6 `}
    >
      {!availabilityItem?.isBool ? (
        <span className={`block text-base font-bold ${index === 0 ? '!text-lg text-[#004435]' : 'text-[#FF847D]'}`}>
          {availabilityItem?.availabilityText}
        </span>
      ) : (
        <>
          {availabilityItem.featureText !== 'Button' && (
            <div className={`flex items-center ${index === 0 ? 'justify-center md:justify-start' : 'justify-center'}`}>
              {availabilityItem.availability ? (
                <img
                  src={BlueTick}
                  alt={availableFeatureAltText}
                  className={`${index === 0 ? ' sm:mr-2 w-4 h-4 lg:h-8 lg:w-8 md:mr-4' : 'w-4 h-4 lg:h-8 lg:w-8'}`}
                  style={{ minWidth: index === 0 ? '2rem' : 'auto', maxWidth: index === 0 ? '2rem' : 'auto' }}
                  loading="lazy"
                />
              ) : (
                <img
                  src={PinkCross}
                  alt={unavailableFeatureAltText}
                  className={`${index === 0 ? 'sm:mr-2 w-4 h-4 lg:h-8 lg:w-8 md:mr-4' : 'w-4 h-4 lg:h-8 lg:w-8'}`}
                  style={{ margin: '0 auto' }}
                  loading="lazy"
                />
              )}
              <span className="hidden text-left text-sm sm:block">
                {index === 0 ? availabilityItem.featureText || 'Best dog food' : ''}
              </span>
            </div>
          )}
        </>
      )}
    </td>
  )
}

const ComparisonTable = ({
  competitors,
  features,
  backgroundColor,
  featureTextColor,
  borderColor,
  headerBackgroundColor,
  tableBackgroundColor,
  builderDesktopLogo,
  availableFeatureAltText = '',
  unavailableFeatureAltText = ''
}: Props) => {
  const getWidthClass = () => {
    const num = competitors.length < 4 ? 4 : competitors.length + 1
    return `${100 / num}%`
  }

  const widthSize = getWidthClass()
  const competitorsForMobile = competitors.length >= 3 ? competitors.slice(0, -1) : competitors

  return (
    <div className="py-2" style={{ backgroundColor }}>
      <div
        className="mx-auto hidden w-full max-w-7xl bg-white py-2 sm:px-6 lg:block lg:px-8"
        style={{ backgroundColor }}
      >
        <div className={`comparisonTable ${competitors.length >= 3 ? '' : 'comparisonTableFiveRow'}  `}>
          <table className="h-px w-full">
            <thead style={{ backgroundColor: headerBackgroundColor || '#00000000' }}>
              <tr className="column-limiter" style={{ backgroundColor: headerBackgroundColor || '#00000000' }}>
                <th
                  className="px-6 py-2 text-left text-sm font-medium text-gray-900"
                  scope="col"
                  style={{ backgroundColor: headerBackgroundColor || '#00000000' }}
                ></th>
                {competitors?.map((comp, i) => {
                  return (
                    <th
                      key={i}
                      className={`py-2 text-center text-lg font-medium leading-6 text-gray-900 ${i !== 0 ? 'sm:px-6' : 'sm:px-2'} border-ddGrey border-l`}
                      scope="col"
                      style={{ maxWidth: '200px' }}
                    >
                      <div className={`mx-auto block`}>
                        <CdnImage
                          className={`aspect-auto m-auto hidden md:block ${i !== 1 ? `${competitors.length >= 3 ? 'w-full' : 'w-1/2 xl:w-1/3'}` : 'w-[70%]'} ${i === 0 ? 'w-full' : ''}`}
                          url={i === 0 ? builderDesktopLogo : comp?.builderLogo}
                          alt={comp.altText}
                          steps={[100, 180, 400]}
                          // renderPlaceholder={false}
                          style={{
                            height: 'auto',
                            dpr: 'auto'
                          }}
                        />
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 border-t border-gray-200" style={{ borderColor }}>
              {features?.map((feature, i) => {
                const backgroundColorTR = i === features.length - 1 ? headerBackgroundColor || '#00000000' : undefined
                const backgroundColorTH =
                  i === features.length - 1 ? headerBackgroundColor || '#00000000' : tableBackgroundColor || '#00000000'
                return (
                  <tr key={i} style={{ borderColor, backgroundColor: backgroundColorTR }} className="column-limiter">
                    <th
                      className={`mx-auto flex h-20 md:h-24 lg:h-20 max-w-[100%] flex-row items-center pt-2 text-left text-sm font-normal sm:py-4 sm:px-6 sm:text-base ${
                        i === competitors.length - 1 ? 'rounded-bl-[30px]' : ''
                      }`}
                      scope="row"
                      style={{ color: featureTextColor, minWidth: widthSize, backgroundColor: backgroundColorTH }}
                    >
                      {feature.icon && (
                        <img
                          className="mx-2 h-4 w-4 items-center lg:mr-4 lg:h-10 lg:w-10"
                          src={feature.icon}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      <div className="px-auto text-xs lg:text-base">{feature.featureName}</div>
                    </th>

                    {competitors.map((comp, j) => (
                      <DesktopRow
                        index={j}
                        competitor={comp}
                        feature={feature}
                        featureTextColor={featureTextColor}
                        borderColor={borderColor}
                        widthSize={widthSize}
                        availableFeatureAltText={availableFeatureAltText}
                        unavailableFeatureAltText={unavailableFeatureAltText}
                        backgroundColor={backgroundColorTH}
                      />
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div
        className="mx-auto block w-11/12 lg:w-full max-w-7xl bg-white py-2 sm:px-6 lg:hidden lg:px-8"
        style={{ backgroundColor }}
      >
        <div className={`comparisonTable ${competitors.length >= 3 ? '' : 'comparisonTableFiveRow'}  `}>
          <table className="h-px w-full">
            <thead style={{ backgroundColor: headerBackgroundColor || '#00000000' }}>
              <tr style={{ backgroundColor: headerBackgroundColor || '#00000000' }} className="column-limiter">
                <th className="px-6 py-2 text-left text-sm font-medium text-gray-900 " scope="col"></th>
                {competitorsForMobile?.map((comp, i) => {
                  const backgroundColor = i !== 0 ? headerBackgroundColor || '#00000000' : undefined
                  return (
                    <th
                      key={i}
                      className={`pt-2 text-center text-lg font-medium leading-6 text-gray-900 sm:px-6 border-ddGrey border-l ${
                        i === 0 ? 'px-2 sm:w-40 md:w-56' : ''
                      }`}
                      scope="col"
                      style={{ maxWidth: '120px', backgroundColor }}
                    >
                      {/* Mobile images */}
                      <div className={`mx-auto block`}>
                        <CdnImage
                          className={`aspect-auto m-auto block lg:hidden w-full ${i === 1 ? 'w-3/4 lg:w-full' : ''}`}
                          url={comp.builderLogo}
                          alt={comp.altText}
                          steps={[100, 180]}
                          style={{
                            height: 'auto',
                            dpr: 'auto'
                          }}
                        />
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 border-t border-gray-200" style={{ borderColor: borderColor }}>
              {features?.map((feature, i) => {
                const backgroundColorTR = i === features.length - 1 ? headerBackgroundColor || '#00000000' : undefined
                const backgroundColorTH =
                  i === features.length - 1 ? headerBackgroundColor || '#00000000' : tableBackgroundColor || '#00000000'
                return (
                  <tr key={i} style={{ borderColor, backgroundColor: backgroundColorTR }} className="column-limiter">
                    <th
                      className={`mx-auto flex h-16 sm:h-20 md:h-24 lg:h-20 max-w-[100%] flex-row items-center text-left text-sm font-normal text-gray-500 sm:px-2 sm:text-base`}
                      scope="row"
                      style={{ color: featureTextColor, minWidth: widthSize, backgroundColor: backgroundColorTH }}
                    >
                      {feature.icon && (
                        <img
                          className="mx-2 h-4 w-4 items-center lg:mr-4 lg:h-8 lg:w-8"
                          src={feature.icon}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      <div className="px-auto text-xs lg:text-base">{feature.featureName}</div>
                    </th>

                    {competitorsForMobile.map((comp, j) => (
                      <DesktopRow
                        index={j}
                        competitor={comp}
                        feature={feature}
                        featureTextColor={featureTextColor}
                        borderColor={borderColor}
                        widthSize={widthSize}
                        availableFeatureAltText={availableFeatureAltText}
                        unavailableFeatureAltText={unavailableFeatureAltText}
                        backgroundColor={backgroundColorTH}
                      />
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ComparisonTable

Builder.registerComponent(ComparisonTable, {
  name: 'ComparisonTable',
  inputs: [
    {
      name: 'competitors',
      type: 'list',
      defaultValue: [{ name: 'Different', code: 'different_dog', logo: null, altText: '' }],
      subFields: [
        { name: 'name', type: 'string', defaultValue: 'Different Dog' },
        {
          name: 'code',
          type: 'string',
          defaultValue: 'different_dog',
          helperText: 'This value should be used to link features in the feature set'
        },
        {
          name: 'builderLogo',
          type: 'file',
          allowedFileTypes: ['jpeg', 'png', 'svg']
        },
        { name: 'altText', type: 'text', defaultValue: '' }
      ]
    },
    {
      name: 'features',
      type: 'list',
      subFields: [
        { name: 'featureName', type: 'string', defaultValue: 'Frozen for freshness' },
        {
          name: 'icon',
          type: 'file',
          allowedFileTypes: ['jpeg', 'png', 'svg']
        },
        {
          name: 'availability',
          type: 'list',
          subFields: [
            { name: 'compCode', type: 'string', defaultValue: 'different_dog' },
            { name: 'isBool', type: 'boolean', defaultValue: true },
            { name: 'availabilityText', type: 'string', defaultValue: 'N/A' },
            { name: 'availability', type: 'boolean', defaultValue: true },
            { name: 'featureText', type: 'string', defaultValue: 'Best Dog Food' }
          ],
          defaultValue: [
            {
              compCode: 'different_dog',
              isBool: true,
              availabilityText: 'N/A',
              availability: true,
              featureText: 'Best Dog Food'
            }
          ]
        }
      ],
      defaultValue: [
        {
          featureName: 'Frozen for freshness',
          availability: [
            {
              compCode: 'different_dog',
              isBool: true,
              availabilityText: 'N/A',
              availability: true,
              featureText: 'Best Dog Food'
            }
          ]
        }
      ]
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'headerBackgroundColor',
      type: 'color',
      defaultValue: ''
    },
    {
      name: 'tableBackgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'featureTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'borderColor',
      type: 'color',
      defaultValue: 'gray'
    },
    {
      name: 'builderDesktopLogo',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'availableFeatureAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'unavailableFeatureAltText',
      type: 'text',
      defaultValue: ''
    }
  ]
})
